import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  Package,
  Save,
  Upload,
  ArrowLeft,
  Image,
  DollarSign,
  FileText,
} from "lucide-react";
import { API_ENDPOINTS } from "../config/api";
import apiRequest from "../utils/api";
import { CATEGORY_MAP } from "../utils/categoryMapping";

export const ProductRegister: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    category: "",
    title: "",
    author: "",
    price: "",
    thumbnailUrl: "",
    thumbnailKey: "", // presigned URL에서 받은 key 저장
    description: "",
    productType: "PURCHASE",
    tags: [] as string[],
    validCategory: true,
  });
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  // 파일명을 보안적으로 안전하게 처리하는 함수
  const sanitizeFileName = (fileName: string): string => {
    // 특수문자를 제거하고 안전한 문자만 남김
    return fileName.replace(/[^a-zA-Z0-9.-]/g, "_");
  };

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    if (!file) return;

    setSelectedImage(file);
    setIsUploading(true);

    try {
      // 파일명을 보안적으로 안전하게 처리
      const sanitizedFileName = sanitizeFileName(file.name);

      // presigned URL 요청 (썸네일 이미지용)
      const presignedResponse = await apiRequest(
        API_ENDPOINTS.PRESIGNED_URL.THUMBNAIL,
        {
          method: "POST",
          body: JSON.stringify({
            fileName: sanitizedFileName,
          }),
        }
      );

      if (!presignedResponse.ok) {
        throw new Error("Presigned URL 요청 실패");
      }

      const presignedData = await presignedResponse.json();

      if (!presignedData.success) {
        throw new Error("Presigned URL 생성 실패");
      }

      // presigned URL로 파일 업로드
      const uploadResponse = await fetch(presignedData.data.url, {
        method: "PUT",
        body: file,
        headers: {
          "Content-Type": file.type,
        },
      });

      if (!uploadResponse.ok) {
        throw new Error("파일 업로드 실패");
      }

      // 업로드 성공 시 실제 이미지 URL만 저장 (query parameter 제거)
      const imageUrl = presignedData.data.url.split("?")[0]; // query parameter 제거
      setFormData((prev) => ({
        ...prev,
        thumbnailKey: presignedData.data.key,
        thumbnailUrl: imageUrl, // 실제 이미지 URL만 저장
      }));
    } catch (error) {
      console.error("이미지 업로드 오류:", error);
      alert("이미지 업로드에 실패했습니다.");
      setSelectedImage(null);
    } finally {
      setIsUploading(false);
    }
  };

  const handleTagAdd = (tag: string) => {
    if (tag.trim() && !formData.tags.includes(tag.trim())) {
      setFormData((prev) => ({
        ...prev,
        tags: [...prev.tags, tag.trim()],
      }));
    }
  };

  const handleTagRemove = (tagToRemove: string) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.filter((tag) => tag !== tagToRemove),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const submitData = {
      category: formData.category,
      title: formData.title,
      author: formData.author,
      price: Number(formData.price),
      thumbnailUrl: formData.thumbnailUrl, // 썸네일 이미지 URL
      description: formData.description,
      productType: formData.productType,
      tags: formData.tags,
      validCategory: formData.validCategory,
    };

    try {
      // 실제 API 호출
      const response = await apiRequest(API_ENDPOINTS.PRODUCTS, {
        method: "POST",
        body: JSON.stringify(submitData),
      });

      if (response.ok) {
        alert("상품이 성공적으로 등록되었습니다!");
        navigate("/dashboard");
      } else {
        alert("상품 등록에 실패했습니다.");
      }
    } catch (error) {
      console.error("상품 등록 오류:", error);
      alert("상품 등록 중 오류가 발생했습니다.");
    }
  };

  return (
    <div className="space-y-6">
      {/* 헤더 */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            size="icon"
            onClick={() => navigate("/dashboard")}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold">상품 등록</h1>
            <p className="text-muted-foreground">새로운 상품을 등록하세요</p>
          </div>
        </div>
        <Button onClick={handleSubmit}>
          <Save className="h-4 w-4 mr-2" />
          상품 등록
        </Button>
      </div>

      {/* 상품 등록 폼 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* 기본 정보 */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <FileText className="h-5 w-5" />
                <span>기본 정보</span>
              </CardTitle>
              <CardDescription>상품의 기본 정보를 입력하세요</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  상품명 (title) *
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="상품명을 입력하세요"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  저자 (author) *
                </label>
                <input
                  type="text"
                  name="author"
                  value={formData.author}
                  onChange={handleInputChange}
                  placeholder="저자명을 입력하세요"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  상품 설명 *
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="상품에 대한 자세한 설명을 입력하세요"
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    가격 *
                  </label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="number"
                      name="price"
                      value={formData.price}
                      onChange={handleInputChange}
                      placeholder="0"
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    상품 타입 *
                  </label>
                  <select
                    name="productType"
                    value={formData.productType}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    required
                  >
                    <option value="PURCHASE">구매 상품</option>
                    <option value="RENTAL">대여 상품</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  카테고리 *
                </label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  required
                >
                  <option value="">카테고리를 선택하세요</option>
                  {Object.entries(CATEGORY_MAP).map(([key, label]) => (
                    <option key={key} value={key}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>

              {/* 태그 입력 */}
              <div>
                <label className="block text-sm font-medium mb-2">태그</label>
                <div className="space-y-2">
                  <div className="flex space-x-2">
                    <input
                      type="text"
                      placeholder="태그를 입력하세요"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                      onKeyPress={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          const input = e.target as HTMLInputElement;
                          handleTagAdd(input.value);
                          input.value = "";
                        }
                      }}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={(e) => {
                        const input = (e.target as HTMLElement)
                          .previousElementSibling as HTMLInputElement;
                        handleTagAdd(input.value);
                        input.value = "";
                      }}
                    >
                      추가
                    </Button>
                  </div>
                  {formData.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {formData.tags.map((tag, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 text-sm rounded-md"
                        >
                          {tag}
                          <button
                            type="button"
                            onClick={() => handleTagRemove(tag)}
                            className="ml-1 text-blue-600 hover:text-blue-800"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* 이미지 업로드 */}
        <div>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Image className="h-5 w-5" />
                <span>상품 이미지</span>
              </CardTitle>
              <CardDescription>상품 이미지를 업로드하세요</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div
                  className={`border-2 border-dashed rounded-lg p-6 text-center ${
                    isUploading
                      ? "border-blue-300 bg-blue-50"
                      : "border-gray-300"
                  }`}
                >
                  <Upload
                    className={`h-12 w-12 mx-auto mb-4 ${
                      isUploading ? "text-blue-400" : "text-gray-400"
                    }`}
                  />
                  <div className="space-y-2">
                    <p
                      className={`text-sm font-medium ${
                        isUploading ? "text-blue-600" : ""
                      }`}
                    >
                      {isUploading
                        ? "이미지 업로드 중..."
                        : "이미지를 드래그하거나 클릭하여 업로드"}
                    </p>
                    <p className="text-xs text-gray-500">
                      PNG, JPG, GIF 최대 10MB
                    </p>
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="hidden"
                    id="image-upload"
                  />
                  <Button
                    variant="outline"
                    className="mt-4"
                    disabled={isUploading}
                    onClick={() =>
                      document.getElementById("image-upload")?.click()
                    }
                  >
                    {isUploading ? "업로드 중..." : "파일 선택"}
                  </Button>
                </div>

                {selectedImage && (
                  <div className="space-y-2">
                    <p className="text-sm font-medium">선택된 파일:</p>
                    <div className="flex items-center space-x-2 p-2 bg-gray-50 rounded-md">
                      <Image className="h-4 w-4 text-gray-500" />
                      <span className="text-sm">{selectedImage.name}</span>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* 미리보기 */}
          <Card className="mt-6">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Package className="h-5 w-5" />
                <span>상품 미리보기</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="border rounded-lg p-4 space-y-3">
                <div className="aspect-square bg-gray-100 rounded-md flex items-center justify-center">
                  {formData.thumbnailUrl ? (
                    <img
                      src={formData.thumbnailUrl}
                      alt="상품 미리보기"
                      className="w-full h-full object-cover rounded-md"
                    />
                  ) : (
                    <Image className="h-12 w-12 text-gray-400" />
                  )}
                </div>
                <div className="space-y-1">
                  <h3 className="font-medium">{formData.title || "상품명"}</h3>
                  <p className="text-sm text-gray-600">
                    저자: {formData.author || "저자명"}
                  </p>
                  <p className="text-sm text-gray-600">
                    카테고리:{" "}
                    {formData.category
                      ? CATEGORY_MAP[
                          formData.category as keyof typeof CATEGORY_MAP
                        ] || formData.category
                      : "카테고리"}
                  </p>
                  <p className="text-sm text-gray-600">
                    {formData.description || "상품 설명"}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-lg">
                      {formData.price
                        ? `₩${Number(formData.price).toLocaleString()}`
                        : "₩0"}
                    </span>
                    <span className="text-sm text-gray-500">
                      {formData.productType === "PURCHASE" ? "구매" : "대여"}
                    </span>
                  </div>
                  {formData.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {formData.tags.map((tag, index) => (
                        <span
                          key={index}
                          className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded"
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};
